<template>
    <div class="sidebar flex overflow-hidden max-w-dashboard mx-auto">
        <div v-if="hasActiveDirectory" class="fixed top-24 h-full z-[35] flex flex-shrink-0 bg-gray-100">
            <div
                class="flex flex-col border-r border-gray-300 transition-width duration-300"
                :class="{ 'w-[270px]': !isMenuCollapsed || (isMenuCollapsed && isHovered), 'w-[60px]': isMenuCollapsed && !isHovered }"
                @mouseover="isHovered = true"
                @mouseout="isHovered = false"
            >
                <div class="flex flex-col h-0 flex-1">
                    <div class="navbar-container flex-1 flex flex-col space-y-8 overflow-y-auto overflow-x-hidden">
                        <nav class="py-8 space-y-1 divide-y divide-gray-300">
                            <div v-for="(sidebarGroup, groupIndex) of sidebarRouteGroups" :key="groupIndex" class="py-3">
                                <div v-for="(sidebarRoute, routeIndex) of sidebarGroup" :key="routeIndex">
                                    <router-link
                                        :to="sidebarRoute.path"
                                        :class="{
                                            'text-gray-500 font-light border-l border-transparent': $route.path !== sidebarRoute.path,
                                            'text-black font-medium bg-primary-700 bg-opacity-10 border-l border-primary-700': $route.path === sidebarRoute.path,
                                        }"
                                        style="grid-template-columns: 27px auto 27px"
                                        class="w-full transition-colors tracking-tight grid items-center px-4 py-4 gap-x-2 h-[56px]"
                                    >
                                        <Icon :src="$route.path === sidebarRoute.path || (hasChildrenActive(sidebarRoute.children) && !isHovered) ? sidebarRoute.activeIcon : sidebarRoute.inactiveIcon"></Icon>
                                        <span class="transition-opacity duration-300 whitespace-nowrap flex items-center gap-1.5" :class="!isMenuCollapsed || isHovered ? 'opacity-100' : 'opacity-0 hidden'">
                                            <span>{{ sidebarRoute.text ? $t(sidebarRoute.text) : sidebarRoute.name }}</span>
                                            <BetaBadge v-if="sidebarRoute.beta" />
                                            <AdminBadge v-if="sidebarRoute.adminOnly" />
                                        </span>
                                        <span v-if="Array.isArray(sidebarRoute.children) && sidebarRoute.children.length && (!isMenuCollapsed || isHovered)" class="justify-self-end">
                                            <ExpandIcon :expand="sidebarRoute.expanded" color="black"></ExpandIcon>
                                        </span>
                                    </router-link>
                                    <div
                                        :style="sidebarRoute.expanded && (!isMenuCollapsed || isHovered) ? `max-height: ${childrenHeight(sidebarRoute)}px;` : 'max-height: 0px;'"
                                        class="overflow-hidden"
                                        style="transition: max-height 0.25s"
                                    >
                                        <div v-for="(sidebarRouteChild, routeChildIndex) of sidebarRoute.children" :key="routeChildIndex">
                                            <router-link
                                                :tabindex="sidebarRoute.expanded ? null : '-1'"
                                                :to="sidebarRouteChild.path"
                                                :class="isPathWithin(sidebarRouteChild.path) ? 'text-black font-medium bg-primary-700 bg-opacity-10 border-l border-primary-700' : 'text-gray-500 font-light'"
                                                style="grid-template-columns: 27px auto 27px"
                                                class="w-full grid tracking-tight items-center px-4 py-2 gap-x-2"
                                            >
                                                <span></span>
                                                <span class="whitespace-nowrap flex items-center gap-1.5">
                                                    <span>{{ sidebarRouteChild.text ? $t(sidebarRouteChild.text) : sidebarRouteChild.name }}</span>
                                                    <BetaBadge v-if="sidebarRouteChild.beta" />
                                                    <AdminBadge v-if="sidebarRouteChild.adminOnly" />
                                                </span>
                                                <span v-if="sidebarRouteChild.children.length" class="justify-self-end">
                                                    <ExpandIcon :expand="sidebarRouteChild.expanded" color="black"></ExpandIcon>
                                                </span>
                                            </router-link>
                                            <div
                                                v-if="sidebarRouteChild.children.length"
                                                :style="sidebarRouteChild.expanded ? `max-height: ${40 * sidebarRouteChild.children.length}px;` : 'max-height: 0px;'"
                                                class="overflow-hidden"
                                                style="transition: max-height 0.25s"
                                            >
                                                <router-link
                                                    v-for="(sidebarRouteGrandchild, routeGrandchildIndex) of sidebarRouteChild.children"
                                                    :key="routeGrandchildIndex"
                                                    :tabindex="sidebarRoute.expanded && sidebarRouteChild.expanded ? null : '-1'"
                                                    :to="sidebarRouteGrandchild.path"
                                                    :class="isPathWithin(sidebarRouteGrandchild.path) ? 'text-black font-medium bg-primary-700 bg-opacity-10 border-l border-primary-700' : 'text-gray-500 font-light'"
                                                    style="grid-template-columns: 27px auto 27px"
                                                    class="w-full grid tracking-tight items-center px-4 py-2 gap-x-2"
                                                >
                                                    <span></span>
                                                    <span class="whitespace-nowrap flex items-center gap-1.5 pl-4">
                                                        <span>{{ sidebarRouteGrandchild.text ? $t(sidebarRouteGrandchild.text) : sidebarRouteGrandchild.name }}</span>
                                                        <BetaBadge v-if="sidebarRouteGrandchild.beta" />
                                                        <AdminBadge v-if="sidebarRouteGrandchild.adminOnly" />
                                                    </span>
                                                </router-link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </nav>
                        <div>
                            <button class="w-full text-sm text-gray-500 h-12 flex items-center px-4 py-3 hover:bg-gray-200 rounded-l-md transition-colors" @click="toggleMenuCollapsed">
                                <ExpandIcon class="flex-shrink-0 transform mx-2 opacity-50" :class="isMenuCollapsed ? '-rotate-90' : 'rotate-90'" color="black"></ExpandIcon>
                                <span v-show="!isMenuCollapsed || isHovered" class="whitespace-nowrap">{{
                                    isMenuCollapsed ? $t('layouts.dashboard.sideBar.buttons.expandMenu') : $t('layouts.dashboard.sideBar.buttons.collapseMenu')
                                }}</span>
                            </button>
                        </div>
                        <div class="w-full pb-4 transition-opacity transition-width duration-300" :class="{ 'opacity-0 w-0': !isLogoShown && !isHovered, 'w-full': isLogoShown || isHovered }">
                            <Icon class="w-36 pl-2.75" :src="SIDEBAR_LOGO_SOURCE"></Icon>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="w-full mt-24 transition-margin-left min-w-0 overflow-x-auto duration-300" :class="{ 'ml-[60px]': isMenuCollapsed && hasActiveDirectory, 'ml-[270px]': !isMenuCollapsed && hasActiveDirectory }">
            <slot></slot>
            <slot name="footer"></slot>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { RouteRecordRaw } from 'vue-router';
import { routes } from '@/plugins/router/routes';
import ExpandIcon from '@/components/icons/ExpandIcon.vue';
import BetaBadge from '@/components/ui/BetaBadge.vue';
import AdminBadge from '@/components/ui/AdminBadge.vue';
import { store } from '@/plugins/store';
import { Module } from '@/types';
import { useNarrowViewportAutoCollapse } from '@/composables/useNarrowViewportAutoCollapse';

interface SidebarRouteData {
    name: string | symbol | undefined;
    path: string;
    children: SidebarRouteData[];
    sidebarGroup: number | undefined;
    activeIcon: string | undefined;
    inactiveIcon: string | undefined;
    expanded: boolean;
    text: string | undefined;
    beta: boolean | undefined;
    adminOnly: boolean | undefined;
}

export default defineComponent({
    components: { ExpandIcon, BetaBadge, AdminBadge },
    setup() {
        return useNarrowViewportAutoCollapse();
    },
    data() {
        return {
            SIDEBAR_LOGO_SOURCE: `/solutions/${process.env.VUE_APP_SOLUTION_PATH}/assets/images/sidebar-logo.svg`,
            isHovered: false,
            ENABLED_REALTIME_ONLY_FEED_ROUTES: ['FeedRawMaterials', 'FeedMixes'],
        };
    },
    computed: {
        sidebarRouteGroups(): SidebarRouteData[][] {
            const DASHBOARD_ROUTE_NAME = 'Dashboard';
            const dashboardRoutes = routes.find((currentRoute) => currentRoute.name === DASHBOARD_ROUTE_NAME)?.children;
            if (!dashboardRoutes || dashboardRoutes.length === 0) {
                return [];
            }

            const hasRealtimeOnlyModule = store.getters.hasRealtimeOnlyModule;

            const sidebarRouteGroups = [
                ...dashboardRoutes
                    .filter(
                        (currentRoute) =>
                            currentRoute.meta?.appearsInSidebar &&
                            this.passesAccessGates(currentRoute) &&
                            // If we are in RealtimeOnly mode, only show Monitoring-related routes
                            (!hasRealtimeOnlyModule || currentRoute.name === 'Monitoring' || currentRoute.name === 'MonitoringUnlock' || currentRoute.name === 'Feed')
                    )
                    .map((currentSidebarRoute) => this.getRouteDataFromRoute(currentSidebarRoute))
                    .reduce((accum: Map<number, SidebarRouteData[]>, current: SidebarRouteData) => {
                        if (!current.sidebarGroup) {
                            return accum;
                        }
                        if (!Array.isArray(accum.get(current.sidebarGroup))) {
                            accum.set(current.sidebarGroup, []);
                        }
                        (accum.get(current.sidebarGroup) as SidebarRouteData[]).push(current);

                        return accum;
                    }, new Map()),
            ].map((sidebarGroup) => sidebarGroup[1]);

            return sidebarRouteGroups;
        },
        hasActiveDirectory() {
            return store.getters.hasActiveDirectory;
        },
    },
    methods: {
        // hideIfNotGetter and requiresModule must both pass, unless the route opts into accessMode 'any' (either is enough).
        passesAccessGates(route: RouteRecordRaw): boolean {
            const getter = route.meta?.hideIfNotGetter;
            const module = route.meta?.requiresModule;
            const getterPasses = typeof getter !== 'string' || Boolean((store.getters as any)[getter]);
            const modulePasses = typeof module !== 'string' || store.getters.hasModuleEnabled(module as Module);
            if (route.meta?.accessMode === 'any' && typeof getter === 'string' && typeof module === 'string') {
                return getterPasses || modulePasses;
            }
            return getterPasses && modulePasses;
        },
        getRouteDataFromRoute(route: RouteRecordRaw): SidebarRouteData {
            const hasRealtimeOnlyModule = store.getters.hasRealtimeOnlyModule;

            return {
                name: route.name,
                path: route.path,
                children:
                    route.children
                        ?.filter(
                            (currentChildRoute) =>
                                currentChildRoute.meta?.appearsInSidebar &&
                                this.passesAccessGates(currentChildRoute) &&
                                // If we are in RealtimeOnly mode, only show Monitoring-related child routes
                                (!hasRealtimeOnlyModule || route.name === 'Monitoring' || (route.name === 'Feed' && this.ENABLED_REALTIME_ONLY_FEED_ROUTES.includes(currentChildRoute.name as string)))
                        )
                        .map((currentChildRoute) => this.getRouteDataFromRoute(currentChildRoute)) || [],
                sidebarGroup: route.meta?.sidebarGroup as number | undefined,
                text: route.meta?.sidebarText as string | undefined,
                beta: route.meta?.beta as boolean | undefined,
                adminOnly: route.meta?.adminOnly as boolean | undefined,
                activeIcon: route.meta?.sidebarActiveIcon as string | undefined,
                inactiveIcon: route.meta?.sidebarInactiveIcon as string | undefined,
                expanded: this.$route.path === route.path || this.$route.meta?.sidebarActivePath === route.path || this.hasActiveDescendant(route),
            };
        },
        toggleMenuCollapsed() {
            this.isMenuCollapsed = !this.isMenuCollapsed;

            if (window.localStorage) {
                localStorage.setItem('menu-collapsed', this.isMenuCollapsed.toString());
            }

            setTimeout(() => {
                this.isLogoShown = !this.isMenuCollapsed;

                window.dispatchEvent(new Event('resize'));
            }, 300);
        },
        /** Whether the current page is one of the route's children, or any level below them. */
        hasActiveDescendant(route: RouteRecordRaw): boolean {
            return !!route.children?.some((currentChildRoute) => this.isPathWithin(currentChildRoute.path) || this.hasActiveDescendant(currentChildRoute));
        },
        /** Height of a group's open child list: one row per child, plus one per row of every child that is itself open. */
        childrenHeight(route: SidebarRouteData) {
            return route.children.reduce((height, child) => height + 40 + (child.expanded ? 40 * child.children.length : 0), 0);
        },
        hasChildrenActive(children: SidebarRouteData[]) {
            return children.some((child) => this.isPathWithin(child.path));
        },
        /** Whether the sidebar entry at `path` stands for the current page: the page itself, or the list a detail page names in `meta.sidebarActivePath`. */
        isPathWithin(path: string) {
            return (this.$route.meta?.sidebarActivePath ?? this.$route.path) === path;
        },
    },
});
</script>

<style>
.sidebar {
    min-height: calc(100vh - 6rem);
}
.navbar-container {
    max-height: calc(100vh - 6rem);
}
</style>
